import { useReadOnly } from '../../Hooks/useReadOnly';
import { Permissions } from '../../Security/permissions';
import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Form, Button, Card } from 'react-bootstrap';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { loginRequest } from '../../authConfig';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import { NotificationsDataService } from '../../Services/NotificationsDataService';
import { UsersDataService } from '../../Services/UsersDataService';
import Select from 'react-select';
import './AdminNotificationsPage.scss';
import { formatGuatemalanDateTime } from '../../Utils/dateFormat';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlasses, faTrash } from '@fortawesome/free-solid-svg-icons';
import Table from '../../Components/Table/Table';
import InformationModal from '../../Components/InformationModal/InformationModal';

const AdminNotificationsContent = () => {
    const readOnly = useReadOnly(Permissions.Collaboration.AdminNotifications);
    const [notifications, setNotifications] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [showInformationModal, setShowInformationModal] = useState(false);
    const [formData, setFormData] = useState({
        date: '',
        title: '',
        suppliers: [],
        users: [],
        urgencyLevel: '',
        sendViaEmail: false,
        message: '',
        attachment: null,
        allUsers: false,
    });
    const fileInputRef = useRef(null);
    const { instance, accounts } = useMsal();

    useEffect(() => {
        fetchNotifications();
        fetchSuppliers();
        fetchUsers();
    }, []);

    const fetchNotifications = async () => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);

            const data = await NotificationsDataService.getNotifications(response.accessToken);
            setNotifications(data);
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        }
    };

    const fetchUsers = async () => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);

            const data = await UsersDataService.getUsers(response.accessToken);
            setUsers(data.map(user => ({ value: user.userId, label: user.email, supplierId: user.supplierId })));
            setFilteredUsers(data.map(user => ({ value: user.userId, label: user.email, supplierId: user.supplierId })));
        } catch (error) {
            console.error('Failed to fetch users:', error);
        }
    };

    const fetchSuppliers = async () => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);

            const data = await UsersDataService.getSuppliers(response.accessToken);
            const supplierOptions = data.map(supplier => ({ value: supplier.supplierId, label: supplier.supplierDsc }));
            setSuppliers([{ value: 'none', label: 'None' }, ...supplierOptions]);
        } catch (error) {
            console.error('Failed to fetch suppliers:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleSupplierChange = (selectedOptions) => {
        setFormData((prevState) => ({
            ...prevState,
            suppliers: selectedOptions || [],
            users: [],
        }));

        if (!selectedOptions || selectedOptions.length === 0) {
            setFilteredUsers(users);
        } else {
            const selectedSupplierIds = selectedOptions.map(option => option.value);
            const filtered = users.filter(user => selectedSupplierIds.includes(user.supplierId) || selectedSupplierIds.includes('none') && !user.supplierId);
            setFilteredUsers(filtered);
        }
    };

    const handleMultiSelectChange = (selectedOptions) => {
        setFormData((prevState) => ({
            ...prevState,
            users: selectedOptions || [],
        }));
    };

    const handleAllUsersChange = (e) => {
        setFormData((prevState) => ({
            ...prevState,
            allUsers: e.target.checked,
            users: e.target.checked ? [] : prevState.users,
        }));
    };

    const handleFileChange = (e) => {
        setFormData((prevState) => ({
            ...prevState,
            attachment: e.target.files[0],
        }));
    };
    

    const handleSubmit = async (e) => {
        if (readOnly) { e?.preventDefault?.(); return; }
        e.preventDefault();
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);
    
            // Create a FormData object to handle file uploads
            const data = new FormData();
            data.append("date", formData.date);
            data.append("title", formData.title);
            data.append("urgencyLevel", formData.urgencyLevel);
            data.append("message", formData.message);
            data.append("sendViaEmail", formData.sendViaEmail);
            data.append("allUsers", formData.allUsers);
    
            // Append file if it exists
            if (formData.attachment) {
                data.append("file", formData.attachment);
            }
    
            // Append each userId individually to FormData
            data.users?.forEach(user => {
                formData.append("userIds", user.value); // Add each user ID as a separate entry
            });

            // Append each supplier ID individually to FormData
            data.suppliers?.forEach(supplier => {
                formData.append("suppliers", supplier.value); // Add each supplier ID as a separate entry
            });
    
            var result = await NotificationsDataService.createNotification(data, response.accessToken);
            if(result && result.status == 201){
                setShowInformationModal(true);
                clearForm();
                fetchNotifications(); // Refresh notifications list
            }
        } catch (error) {
            console.error('Failed to create notification:', error);
        }
    };
    

    const handleView = () => {

    }

    const handleDelete = () => {

    }

    const clearForm = () => {
        setFormData({
            date: '',
            title: '',
            suppliers: [],
            users: [],
            urgencyLevel: '',
            sendViaEmail: false,
            message: '',
            attachment: null,
            allUsers: false,
        })
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }

    const columns = React.useMemo(
        () => [
            { header: 'ID', accessorKey: 'id' },
            { header: 'Title', accessorKey: 'title' },
            { header: 'Message', accessorKey: 'message' },
            { header: 'Sent', accessorKey: 'date', cell: info => formatGuatemalanDateTime(info.getValue()) },
            {
                header: 'Actions',
                id: 'actions',
                cell: ({ cell, row }) => 
                    (<div className='text-center text-nowrap'>
                        <Button size='sm' variant='outline-dark' className='me-2' onClick={() => handleView(row.original.id)}><FontAwesomeIcon icon={faGlasses} /></Button>
                        <Button disabled={readOnly} size='sm' variant='outline-danger' onClick={() => handleDelete(row.original.id)}><FontAwesomeIcon icon={faTrash} /></Button>
                    </div>),
                footer: props => props.column.id,
            },
            // {
            //     header: 'Delete',
            //     id: 'delete',
            //     cell: ({ cell, row }) => <div className='text-center'><Button size='sm' variant='outline-danger' onClick={() => handleDelete(row.original.id)}><FontAwesomeIcon icon={faTrash} /></Button></div>,
            //     footer: props => props.column.id,
            // },
        ],
        [readOnly]
    );

    return (
        <Container fluid>
            <Row className='h-100'>
                <Col md={12} className='flex-grow-1 h-50 py-2'>
                    <div className='notifications-container'>
                        <h5>New Notification</h5>
                        <Form onSubmit={handleSubmit} className='overflow-y-auto'>
                            <Row>
                                <Col md={7}>
                                    <Form.Group controlId="title" className='mb-3'>
                                        <Form.Label>Title</Form.Label>
                                        <Form.Control disabled={readOnly} className='form-control-mnml' size='sm' type="text" name="title" value={formData.title} onChange={handleChange} />
                                    </Form.Group>
                                    <Row className='mb-3'>
                                        <Form.Group as={Col} controlId="date">
                                            <Form.Label>Date</Form.Label>
                                            <Form.Control disabled={readOnly} className='form-control-mnml' size='sm' type="datetime-local" name="date" value={formData.date} onChange={handleChange} />
                                        </Form.Group>
                                        <Form.Group as={Col} controlId="urgencyLevel">
                                            <Form.Label>Urgency Level</Form.Label>
                                            <Form.Select disabled={readOnly} className='form-control-mnml' size='sm' as="select" name="urgencyLevel" value={formData.urgencyLevel} onChange={handleChange}>
                                                <option value="">Select Urgency</option>
                                                <option value="Low">Low</option>
                                                <option value="Medium">Medium</option>
                                                <option value="High">High</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Row>
                                    <Form.Group controlId="allUsers" className='mb-3'>
                                        <Form.Check disabled={readOnly} size='sm' type="checkbox" label="All Users" name="allUsers" checked={formData.allUsers} onChange={handleAllUsersChange} />
                                    </Form.Group>
                                    {!formData.allUsers && (
                                        <Row className='mb-3'>
                                            <Form.Group as={Col} controlId="suppliers" className=''>
                                                <Form.Label>Suppliers</Form.Label>
                                                <div className='react-select mnml'>
                                                    <Select isDisabled={readOnly}
                                                        isMulti
                                                        name="suppliers"
                                                        options={suppliers}
                                                        className="basic-multi-select"
                                                        classNamePrefix="select"
                                                        onChange={handleSupplierChange}
                                                        menuPortalTarget={document.body} menuPosition={'fixed'}
                                                    />
                                                </div>
                                            </Form.Group>
                                            <Form.Group as={Col} controlId="user" className=''>
                                                <Form.Label>User</Form.Label>
                                                <div className='react-select mnml'>
                                                    <Select isDisabled={readOnly}
                                                        isMulti
                                                        name="users"
                                                        options={filteredUsers}
                                                        className="basic-multi-select"
                                                        classNamePrefix="select"
                                                        onChange={handleMultiSelectChange}
                                                        menuPortalTarget={document.body} menuPosition={'fixed'}
                                                    />
                                                </div>
                                            </Form.Group>
                                        </Row>
                                    )}
                                </Col>
                                <Col md={5}>
                                    <Form.Group controlId="message" className='mb-3'>
                                        <Form.Label>Message</Form.Label>
                                        <Form.Control disabled={readOnly} className='form-control-mnml' size='sm' as="textarea" name="message" value={formData.message} onChange={handleChange} />
                                    </Form.Group>
                                    <Form.Group controlId="sendViaEmail" className='mb-3'>
                                        <Form.Check disabled={readOnly} size='sm' type="checkbox" label="Send via email" name="sendViaEmail" checked={formData.sendViaEmail} onChange={(e) => setFormData((prevState) => ({ ...prevState, sendViaEmail: e.target.checked }))} />
                                    </Form.Group>
                                    <Form.Group controlId="attachment" className='mb-3'>
                                        <Form.Label>Upload files</Form.Label>
                                        <Form.Control disabled={readOnly} ref={fileInputRef} className='form-control-mnml' size='sm' type="file" name="attachment" onChange={handleFileChange} />
                                    </Form.Group>
                                    <div className='w-100 d-flex justify-content-end'>
                                        <Button disabled={readOnly} variant="primary" size='sm' type="submit" className="mt-2">Send Notification</Button>
                                    </div>
                                </Col>
                            </Row>
                        </Form>
                    </div>
                </Col>
                <Col md={12} className='flex-grow-1 h-50 py-2'>
                    <div className="notifications-container">
                        <h5>Previous Notifications</h5>
                        <Table columns={columns} data={notifications} tableClass="table-hover table-sm" containerClass="table-responsive" headerClass="table-light" />
                    </div>
                </Col>
            </Row>
            <InformationModal
                show={showInformationModal}
                onHide={() => setShowInformationModal(false)}
                onAcceptClick={() => setShowInformationModal(false)}
                title="Notification sent"
                content="A new notification has been created and sent to the selected users."
            />
        </Container>
    );
};

const AdminNotificationsPage = () => {
    return (
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
            <PageLayout2 title="Notification Manager">
                <AdminNotificationsContent />
            </PageLayout2>
        </MsalAuthenticationTemplate>
    );
};

export default AdminNotificationsPage;
